import { useState } from "react";
import { CheckCircle2, AlertTriangle, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { fmtEUR, fmtSigned, parseAmount, toInputAmount } from "../lib/format";

const Row = ({ label, value, strong, testId }) => (
  <div className="flex flex-wrap items-baseline justify-between gap-x-4 py-1">
    <span className="min-w-0 text-sm text-slate-600">{label}</span>
    <span data-testid={testId} className={`font-num ml-auto whitespace-nowrap text-right text-[15px] ${strong ? "font-semibold text-slate-900" : "text-slate-800"}`}>{value}</span>
  </div>
);

const FIELDS = [
  ["opening_balance", "Alter Kontostand"],
  ["closing_balance", "Neuer Kontostand"],
  ["printed_total_credits", "Gutschriften (gedruckt)"],
  ["printed_total_debits", "Belastungen (gedruckt, negativ)"],
];

const EditBalances = ({ st, open, setOpen, onSave }) => {
  const [vals, setVals] = useState({});
  const init = () => setVals(Object.fromEntries(FIELDS.map(([k]) => [k, toInputAmount(st[k])])));
  return (
    <Dialog open={open} onOpenChange={(o) => { if (o) init(); setOpen(o); }}>
      <DialogContent data-testid="edit-balances-dialog">
        <DialogHeader><DialogTitle>Salden korrigieren</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          {FIELDS.map(([k, label]) => (
            <div key={k} className="grid gap-1.5">
              <Label htmlFor={k}>{label}</Label>
              <Input id={k} data-testid={`balance-input-${k}`} className="font-num" value={vals[k] ?? ""} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
          <Button data-testid="save-balances-btn" onClick={() => onSave(Object.fromEntries(FIELDS.map(([k]) => [k, parseAmount(vals[k])])))}>Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const BalanceCheck = ({ st, onSave }) => {
  const [open, setOpen] = useState(false);
  const b = st.balance_check || {};
  const ok = b.ok;
  return (
    <div data-testid="balance-check-card" className={`rounded-xl border bg-white p-5 ${ok ? "border-emerald-200" : "border-red-200"}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        {ok ? (
          <div data-testid="saldo-check-badge" data-ok="true" className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-1.5 text-[15px] font-semibold text-emerald-800">
            <CheckCircle2 className="h-5 w-5" /> Saldo stimmt
          </div>
        ) : (
          <div data-testid="saldo-check-badge" data-ok="false" className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-1.5 text-[15px] font-semibold text-red-800">
            <AlertTriangle className="h-5 w-5" />
            {b.difference !== null && b.difference !== undefined ? `Saldo weicht ab: ${fmtSigned(b.difference)}` : "Saldo nicht prüfbar – Kontostand fehlt"}
          </div>
        )}
        <Button variant="ghost" size="sm" data-testid="edit-balances-btn" onClick={() => setOpen(true)} className="gap-1.5 text-slate-600">
          <Pencil className="h-3.5 w-3.5" /> Salden
        </Button>
      </div>
      <Row label="Alter Kontostand" value={fmtEUR(b.opening_balance)} testId="opening-balance" />
      <Row label={`+ Summe ${b.transaction_count} Buchungen`} value={fmtSigned(b.sum)} testId="transactions-sum" />
      <div className="my-1 border-t border-dashed border-slate-200" />
      <Row label="= Berechneter Kontostand" value={fmtEUR(b.expected_closing)} strong testId="expected-closing" />
      <Row label="Neuer Kontostand laut Auszug" value={fmtEUR(b.closing_balance)} strong testId="closing-balance" />
      {(st.printed_total_credits !== null || st.printed_total_debits !== null) && (
        <div className="mt-3 grid grid-cols-1 gap-2 border-t border-slate-100 pt-3 text-xs sm:grid-cols-2">
          <Totals label="Gutschriften" printed={st.printed_total_credits} sum={b.credits_sum} ok={b.credits_ok} testId="credits-check" />
          <Totals label="Belastungen" printed={st.printed_total_debits} sum={b.debits_sum} ok={b.debits_ok} testId="debits-check" />
        </div>
      )}
      <EditBalances st={st} open={open} setOpen={setOpen} onSave={async (v) => { await onSave(v); setOpen(false); }} />
    </div>
  );
};

const Totals = ({ label, printed, sum, ok, testId }) => (
  <div data-testid={testId} className={`rounded-md px-2.5 py-1.5 ${ok === false ? "bg-red-50 text-red-800" : "bg-slate-50 text-slate-700"}`}>
    <div><span className="font-semibold">{label}:</span>{" "}<span className="font-num whitespace-nowrap">{fmtEUR(sum)}</span></div>
    {printed !== null && printed !== undefined && <div>gedruckt <span className="font-num whitespace-nowrap">{fmtEUR(printed)}</span> {ok ? "✓" : "✗"}</div>}
  </div>
);
